import { MINUTES_PER_DAY, type AttentionLevel, type CommentDraft, type DurationMode, type LifeSpace, type RecurrenceRule, type ReminderDraft, type Section, type Tag, type Task } from '../../db/models'
import type { ImportedRows } from '../../dataSource/dataSource'
import { MAX_SUBTASK_DEPTH } from '../../db/taskTree'
import { computeAppendOrders } from '../../db/appendOrders'
import { MAX_PROJECT_DEPTH } from '../../projectTree'
import { formatImportedFrom } from '../importedFrom'
import { lifeSpaceFor, type LifeSpaceChoices } from './lifeSpaces'
import { translateRecurrence } from './recurrence'
import { mapDate } from './mapDate'
import { buildReminderDrafts, remindersByItem, withAutoReminder } from './reminders'
import { TODOIST_PROJECT_ICON, projectIconColor, tagColor, todoistHex } from './colors'
import type { TodoistItem, TodoistNote, TodoistProject, TodoistProjectNote, TodoistSnapshot } from './types'

/**
 * **Der Todoist-Import als Plan** (T07, #172) — was angelegt wird, mit welchen
 * Feldern, in welcher Reihenfolge; ohne Datenbank. `runTodoistImport` legt es
 * danach an. Die Zuordnung steht in `.claude/tasks/todoist-import-t07-write.md`
 * und im Konzept (`docs/concept/todoist-import.md`).
 *
 * Schon Übernommenes (`importedFrom`, T01) plant er nicht noch einmal: Es
 * bleibt, wie es in HappyTask inzwischen ist.
 */

/** Verweis auf eine Zeile: schon da (`id`) oder in diesem Import angelegt (`key` der geplanten Zeile). */
export type Ref = { id: string } | { key: string }

export interface PlannedProject {
  importedFrom: string
  name: string
  /**
   * Das Elternprojekt (#185): schon da (`id`), in diesem Import angelegt (`key`) oder `null` =
   * oben im Lebensbereich. Eltern stehen im Plan vor ihren Kindern.
   */
  parent: Ref | null
  lifeSpace: LifeSpace
  archived: boolean
  /** Todoists `#` in der ähnlichsten Icon-Farbe (T08). */
  icon: string
  iconColor: string | null
  /** Todoists Hex, treu übernommen — HappyTask zeigt `Project.color` (noch) nicht. */
  color: string | null
  /** Die Projekt-Kommentare aus Todoist (`project_notes`, #184). */
  comments: CommentDraft[]
  /**
   * **Favorit** (#194/#210): Todoists `is_favorite`. Todoist kennt keinen Zeitpunkt — die Stellen
   * folgen seiner Projekt-Reihenfolge, unten an „Favoriten“ angehängt. `null` = kein Favorit.
   */
  favoriteOrder: number | null
}

/** Todoists `archived_at` als Datum; ohne (oder unlesbar) „jetzt“ — archiviert ist er ja. */
function archivedAtOf(raw: string | null | undefined): Date {
  const at = raw ? new Date(raw) : null
  return at && !Number.isNaN(at.getTime()) ? at : new Date()
}

export interface PlannedSection {
  importedFrom: string
  project: Ref
  title: string
  collapsed: boolean
  /** `null` = hinter das Vorhandene — nur, wenn die Lage eines schon übernommenen Projekts fehlt (#317). */
  order: number | null
  /** Todoists Beschreibung (#192) — gespeichert, noch nicht angezeigt. `null` = keine. */
  description: string | null
  /** Archiviert seit (#193, `archived_at`); fehlt der Zeitpunkt bei `is_archived`, gilt „jetzt“. `null` = aktiv. */
  archivedAt: Date | null
}

export interface PlannedTag {
  key: string
  /** `null` bei Labels, die Todoist nur an Aufgaben nennt (geteilte Labels). */
  importedFrom: string | null
  name: string
  /** `null` = die übliche Reihenfolge (`nextTagColor`). */
  color: string | null
}

/** Eine Unteraufgabe trägt, was eine Aufgabe trägt (T14 #179), Ort vom Elternteil. */
export interface PlannedSubtask {
  importedFrom: string
  title: string
  done: boolean
  completedAt: Date | null
  description: string | null
  attention: AttentionLevel | null
  startDate: Date | null
  startTime: string | null
  dueDate: Date | null
  estimatedDuration: number | null
  durationMode: DurationMode | null
  /** Ihre eigene Wiederholung (#205) — nicht, wenn sich eine Ebene darüber wiederholt (#181). */
  recurrence: RecurrenceRule | null
  /** Todoists Zähler (#198), siehe {@link mapCounters}. */
  completedCount: number | null
  postponedCount: number | null
  tags: Ref[]
  /** Ihre Kommentare aus Todoist (#183). */
  comments: CommentDraft[]
  /** Ihre Erinnerungen aus Todoist (Plan task-reminders), dazu die Standard-Erinnerung für Todoists automatische. */
  reminders: ReminderDraft[]
  /** Ihre Unteraufgaben, eine Ebene tiefer (#181) — auf Ebene 4 leer. */
  subtasks: PlannedSubtask[]
}

export interface PlannedTask {
  importedFrom: string
  /** `null` = ohne Projekt, also „Kurz notiert“ (Todoists Inbox). */
  project: Ref | null
  section: Ref | null
  lifeSpace: LifeSpace
  title: string
  description: string | null
  attention: AttentionLevel | null
  startDate: Date | null
  startTime: string | null
  dueDate: Date | null
  estimatedDuration: number | null
  durationMode: DurationMode | null
  recurrence: RecurrenceRule | null
  /** Todoists Zähler (#198), siehe {@link mapCounters}. */
  completedCount: number | null
  postponedCount: number | null
  completedAt: Date | null
  /** `null` = die Datenbank hängt hinten an: ohne Projekt, oder die Lage des übernommenen Projekts fehlt (#317). */
  order: number | null
  tags: Ref[]
  /** Die Kommentare aus Todoist (#183). */
  comments: CommentDraft[]
  /** Die Erinnerungen aus Todoist (Plan task-reminders), dazu die Standard-Erinnerung für Todoists automatische. */
  reminders: ReminderDraft[]
  subtasks: PlannedSubtask[]
}

/**
 * Was ein erneuter Import an einer schon übernommenen Zeile nachträgt — nur Felder, die dort leer
 * sind: Tages-Dauern (T32 #197) und Todoists Zähler (#198).
 */
export interface TaskFill {
  id: string
  changes: Partial<Pick<Task, 'estimatedDuration' | 'durationMode' | 'completedCount' | 'postponedCount'>>
}

export interface ImportPlan {
  projects: PlannedProject[]
  sections: PlannedSection[]
  tags: PlannedTag[]
  tasks: PlannedTask[]
  /**
   * Vor T32 (#197) kamen Tages-Dauern nicht mit, vor #198 die Zähler, und ein zweiter Import
   * überspringt Übernommenes. Darum hier nachgetragen — je Feld nur, wo in HappyTask nichts steht,
   * und nie an einer gelöschten Zeile. Eine Zeile je Aufgabe.
   */
  taskFills: TaskFill[]
  /** Schon übernommen (oder in HappyTask gelöscht) — bleibt unangetastet. */
  skipped: number
}

/** Texte, die in Beschreibungen und Kommentaren landen — in der Sprache der App. */
export interface PlanTexts {
  attachment: string
  recurrence: string
}

export interface PlanInput {
  snapshot: TodoistSnapshot
  lifeSpaces: LifeSpaceChoices
  imported: ImportedRows
  /** Vorhandene Tags: ein gleichnamiges wird benutzt statt neu angelegt. */
  tags: readonly Tag[]
  texts: PlanTexts
  /**
   * So stehen die schon übernommenen Projekte in HappyTask, je Projekt-ID (#317) — Neues kommt in
   * deren Lücken. Fehlt eine, bleibt `order` dort leer.
   */
  layouts?: ReadonlyMap<string, ProjectLayout>
}

/** Aufgaben und Abschnitte eines Projekts, wie `getTasksByProject` / `getSectionsByProject` sie liefern. */
export interface ProjectLayout {
  tasks: readonly Pick<Task, 'order'>[]
  sections: readonly Pick<Section, 'id' | 'order'>[]
}

const todoist = (id: string) => formatImportedFrom('todoist', id)

export { mapDate }

/** Todoist-Priorität 4 = P1 (höchste) … 1 = P4 (keine). */
export function mapPriority(priority: number): AttentionLevel | null {
  return priority === 4 ? 'high' : priority === 3 ? 'medium' : priority === 2 ? 'low' : null
}

/**
 * Todoists Dauer: Minuten wie sie sind, Tage als Aufwand zu je 8 Std. (T32 #197, CONTEXT.md Dauer).
 */
export function mapDuration(duration: TodoistItem['duration']): { estimatedDuration: number | null; durationMode: DurationMode | null } {
  if (!duration) return { estimatedDuration: null, durationMode: null }
  return duration.unit === 'day'
    ? { estimatedDuration: duration.amount * MINUTES_PER_DAY, durationMode: 'days' }
    : { estimatedDuration: duration.amount, durationMode: 'duration' }
}

/** Beschreibung + nicht nachbaubare Wiederholung. */
/**
 * **Erledigt-Zähler** und **Vertagen** (#198, CONTEXT.md → Zähler). Eine 0 wird `null` — beides
 * heißt „nie gezählt“, und `null` hält die Zeile wie eine selbst angelegte.
 */
export function mapCounters(item: Pick<TodoistItem, 'completed_count' | 'postponed_count'>): { completedCount: number | null; postponedCount: number | null } {
  return { completedCount: item.completed_count || null, postponedCount: item.postponed_count || null }
}

export function buildDescription(item: TodoistItem, texts: PlanTexts, keepRecurrence: boolean): string | null {
  const parts: string[] = []
  const base = item.description?.trim()
  if (base) parts.push(base)
  if (item.due?.is_recurring && !keepRecurrence && item.due.string) parts.push(`${texts.recurrence}: ${item.due.string}`)
  return parts.length > 0 ? parts.join('\n\n') : null
}

/**
 * **Todoist-`notes` werden Kommentare** (#183) — nie Notizen (CONTEXT.md). Ein Anhang bleibt als
 * „[Anhang: datei.pdf]" im Text, bis es echte Anhänge gibt (#188/#189). Ohne Zeitpunkt aus Todoist
 * nimmt die Datenschicht „jetzt"; ein leerer Kommentar ohne Anhang fällt weg.
 */
export function buildComments(notes: readonly Pick<TodoistNote, 'id' | 'content' | 'posted_at' | 'file_attachment'>[], texts: PlanTexts): CommentDraft[] {
  return notes.flatMap(note => {
    const file = note.file_attachment?.file_name ? `[${texts.attachment}: ${note.file_attachment.file_name}]` : ''
    const content = [note.content.trim(), file].filter(Boolean).join(' ')
    if (!content) return []
    const postedAt = note.posted_at ? new Date(note.posted_at) : null
    return [{
      content,
      ...(postedAt && !Number.isNaN(postedAt.getTime()) ? { postedAt } : {}),
      importedFrom: todoist(note.id),
    }]
  })
}

/**
 * Eltern vor Kindern (#185): Die Wurzeln in Todoists Liste, darunter die Kinder nach
 * `child_order`. Ein Zyklus in `parent_id` hängt an keiner Wurzel — seine Glieder kommen hinten
 * an, als wären sie oben.
 */
function parentsFirst(projects: readonly TodoistProject[]): TodoistProject[] {
  const ids = new Set(projects.map(p => p.id))
  const children = new Map<string, TodoistProject[]>()
  for (const project of projects) {
    if (!project.parent_id || !ids.has(project.parent_id)) continue
    children.set(project.parent_id, [...(children.get(project.parent_id) ?? []), project])
  }
  const ordered: TodoistProject[] = []
  const seen = new Set<string>()
  const visit = (project: TodoistProject) => {
    if (seen.has(project.id)) return
    seen.add(project.id)
    ordered.push(project)
    for (const child of [...(children.get(project.id) ?? [])].sort((a, b) => a.child_order - b.child_order)) visit(child)
  }
  for (const project of projects) if (!project.parent_id || !ids.has(project.parent_id)) visit(project)
  for (const project of projects) visit(project)
  return ordered
}

/** Oberste Aufgabe über `parent_id`; fehlt ein Elternteil, ist die Zeile selbst oben. */
function rootOf(item: TodoistItem, byId: Map<string, TodoistItem>): TodoistItem {
  let current = item
  const seen = new Set<string>()
  while (current.parent_id && byId.has(current.parent_id) && !seen.has(current.id)) {
    seen.add(current.id)
    current = byId.get(current.parent_id)!
  }
  return current
}

export function planTodoistImport({ snapshot, lifeSpaces, imported, tags, texts, layouts }: PlanInput): ImportPlan {
  let skipped = 0
  const projectsById = new Map(snapshot.projects.map(p => [p.id, p]))
  const inbox = snapshot.projects.find(p => p.inbox_project) ?? null
  const inboxLifeSpace = inbox ? lifeSpaceFor(lifeSpaces, inbox.id) : lifeSpaceFor(lifeSpaces, '')

  // ── Projekte ──
  const projects: PlannedProject[] = []
  /** `project_notes` je Projekt — werden Kommentare am Projekt (#184). Die der Inbox haben kein Ziel. */
  const notesByProject = new Map<string, TodoistProjectNote[]>()
  for (const note of snapshot.projectNotes) notesByProject.set(note.project_id, [...(notesByProject.get(note.project_id) ?? []), note])
  /** Todoist-Projekt → wohin seine Aufgaben gehen; `undefined` = gar nicht (in HappyTask gelöscht). */
  const projectTarget = new Map<string, Ref | null | undefined>()
  /** Lebensbereich schon übernommener Projekte — gilt statt der (leeren) Wahl in der Vorschau. */
  const existingLifeSpace = new Map<string, LifeSpace>()
  const newProjects = new Set<string>()
  /**
   * Tiefe und Elternprojekt, wie sie in HappyTask ankommen (#185). Ein schon übernommenes Projekt
   * gilt als oben — wo es inzwischen steht, weiß der Plan nicht; wäre es zu tief, rückt das neue
   * Kind beim Anlegen direkt dahinter (`db.createProject`).
   */
  const placed = new Map<string, { depth: number; parent: Ref | null }>()
  // Favoriten in Todoists Reihenfolge (Baum nach `child_order`), angehängt ab jetzt (#194).
  const favoriteBase = Date.now()
  const favoriteRank = new Map(
    parentsFirst([...snapshot.projects].sort((a, b) => a.child_order - b.child_order))
      .filter(project => project.is_favorite && !project.inbox_project)
      .map((project, rank) => [project.id, rank]),
  )
  for (const project of parentsFirst(snapshot.projects)) {
    if (project.inbox_project) {
      projectTarget.set(project.id, null)
      continue
    }
    const origin = todoist(project.id)
    const known = imported.projects.get(origin)
    if (known) {
      skipped++
      projectTarget.set(project.id, known.deleted ? undefined : { id: known.id })
      if (known.lifeSpace) existingLifeSpace.set(project.id, known.lifeSpace)
      placed.set(project.id, { depth: 0, parent: null })
      continue
    }
    const lifeSpace = lifeSpaceFor(lifeSpaces, project.id)
    /*
      Unter seinem Elternprojekt, wenn das im selben Lebensbereich landet — sonst oben in seinem
      Bereich. Tiefer als Ebene 4 rückt es hoch, direkt hinter das Projekt, unter dem es stand:
      Es wird Geschwister seines Elternprojekts und kommt im Plan gleich nach dessen Ast.
    */
    const parentProject = project.parent_id ? projectsById.get(project.parent_id) : undefined
    const parentTarget = parentProject ? projectTarget.get(parentProject.id) : undefined
    const parentLifeSpace = parentProject ? existingLifeSpace.get(parentProject.id) ?? lifeSpaceFor(lifeSpaces, parentProject.id) : null
    const parentPlace = parentProject ? placed.get(parentProject.id) : undefined
    let place: { depth: number; parent: Ref | null } = { depth: 0, parent: null }
    if (parentTarget && parentPlace && parentLifeSpace === lifeSpace) {
      place = parentPlace.depth + 1 < MAX_PROJECT_DEPTH
        ? { depth: parentPlace.depth + 1, parent: parentTarget }
        : parentPlace
    }
    placed.set(project.id, place)
    projects.push({
      importedFrom: origin,
      name: project.name,
      parent: place.parent,
      lifeSpace,
      archived: project.is_archived,
      icon: TODOIST_PROJECT_ICON,
      iconColor: projectIconColor(project.color),
      color: todoistHex(project.color),
      comments: buildComments(notesByProject.get(project.id) ?? [], texts),
      favoriteOrder: favoriteRank.has(project.id) ? favoriteBase + favoriteRank.get(project.id)! : null,
    })
    projectTarget.set(project.id, { key: origin })
    newProjects.add(project.id)
  }

  // ── Tags: schon übernommen → dessen ID; gleichnamig vorhanden → benutzen; sonst neu ──
  const plannedTags: PlannedTag[] = []
  const tagByName = new Map<string, Ref>()
  /** In HappyTask gelöschte, früher übernommene Tags: bleiben weg, auch an neuen Aufgaben. */
  const deletedTags = new Set<string>()
  for (const tag of tags) if (!tag.deletedAt) tagByName.set(tag.name.trim().toLowerCase(), { id: tag.id })
  for (const label of snapshot.labels) {
    const key = label.name.trim().toLowerCase()
    const known = imported.tags.get(todoist(label.id))
    if (known) {
      skipped++
      if (known.deleted) deletedTags.add(key)
      else tagByName.set(key, { id: known.id })
      continue
    }
    if (tagByName.has(key)) continue
    const origin = todoist(label.id)
    plannedTags.push({ key: origin, importedFrom: origin, name: label.name.trim(), color: tagColor(label.color) })
    tagByName.set(key, { key: origin })
  }
  const tagRefs = (names: readonly string[]): Ref[] => names.flatMap(name => {
    const key = name.trim().toLowerCase()
    if (!key || deletedTags.has(key)) return []
    if (!tagByName.has(key)) {
      // Label an einer Aufgabe, das nicht in der Liste steht (geteiltes Label): neu, ohne Herkunft.
      plannedTags.push({ key: `name:${key}`, importedFrom: null, name: name.trim(), color: null })
      tagByName.set(key, { key: `name:${key}` })
    }
    return [tagByName.get(key)!]
  })

  // ── Aufgaben: aktive und erledigte zusammen, damit Unteraufgaben ihre Eltern finden ──
  // Steht eine Aufgabe offen UND im Verlauf (Todoist könnte frühere Runden einer
  // Wiederholung unter derselben ID führen), zählt die offene — sonst entstünden Doppel.
  const activeIds = new Set(snapshot.items.map(i => i.id))
  const allItems = [...snapshot.items, ...snapshot.completedItems.filter(i => !activeIds.has(i.id))]
  const itemsById = new Map(allItems.map(i => [i.id, i]))
  const notesByItem = new Map<string, TodoistNote[]>()
  for (const note of snapshot.notes) notesByItem.set(note.item_id, [...(notesByItem.get(note.item_id) ?? []), note])
  /**
   * **Erinnerungen** (Plan task-reminders): nur an offenen Aufgaben in offenen Projekten — eine
   * erledigte oder archivierte klingelt nicht mehr, in Todoist nicht und hier nicht.
   */
  const reminderRows = remindersByItem(snapshot.reminders)
  const remindersFor = (item: TodoistItem): ReminderDraft[] =>
    item.completed_at || projectsById.get(item.project_id)?.is_archived
      ? []
      : withAutoReminder(
        buildReminderDrafts(reminderRows.get(item.id) ?? [], snapshot.userId),
        !!(item.due && mapDate(item.due.date)?.time),
        snapshot.autoReminder,
      )
  const roots = allItems.filter(i => rootOf(i, itemsById) === i)
  const directChildren = new Map<string, TodoistItem[]>()
  for (const item of allItems) {
    if (item.parent_id && itemsById.has(item.parent_id) && rootOf(item, itemsById) !== item) {
      directChildren.set(item.parent_id, [...(directChildren.get(item.parent_id) ?? []), item])
    }
  }
  /** Alle Nachfahren flach, jede tiefere Zeile direkt hinter ihrem Elternteil. */
  const childrenOf = (id: string, seen = new Set<string>()): TodoistItem[] =>
    (directChildren.get(id) ?? [])
      .filter(child => !seen.has(child.id) && seen.add(child.id))
      .sort((a, b) => a.child_order - b.child_order)
      .flatMap(child => [child, ...childrenOf(child.id, seen)])

  /**
   * **Die Unteraufgaben unter `id` als Baum** (#181), `level` ist ihre Ebene. Bis Ebene 4 bleibt die
   * Tiefe; was darunter liegt, rückt auf Ebene 4 hoch — direkt hinter seinen Vorfahren dort. Schon
   * übernommene Zeilen fallen weg, ihre Kinder rücken an ihren Platz. `locked`: Eine Ebene darüber
   * wiederholt sich — dann gilt deren Rhythmus, und der Satz aus Todoist geht in die Beschreibung
   * (#205, Punkt 5).
   */
  const subtasksOf = (id: string, level: number, locked: boolean, seen = new Set<string>()): PlannedSubtask[] =>
    (directChildren.get(id) ?? [])
      .filter(child => !seen.has(child.id) && seen.add(child.id))
      .sort((a, b) => a.child_order - b.child_order)
      .flatMap((child): PlannedSubtask[] => {
        if (imported.subtasks.has(todoist(child.id))) return subtasksOf(child.id, level, locked, seen)
        const childStart = child.due ? mapDate(child.due.date) : null
        const childDeadline = child.deadline ? mapDate(child.deadline.date) : null
        const childRecurrence = child.completed_at || locked ? null : translateRecurrence(child.due)
        const planned: PlannedSubtask = {
          importedFrom: todoist(child.id),
          title: child.content,
          done: !!child.completed_at,
          completedAt: child.completed_at ? new Date(child.completed_at) : null,
          description: buildDescription(child, texts, childRecurrence !== null),
          comments: buildComments(notesByItem.get(child.id) ?? [], texts),
          reminders: remindersFor(child),
          attention: mapPriority(child.priority),
          startDate: childStart?.day ?? null,
          startTime: childStart?.time ?? null,
          dueDate: childDeadline?.day ?? null,
          ...mapDuration(child.duration),
          ...mapCounters(child),
          recurrence: childRecurrence,
          tags: tagRefs(child.labels),
          subtasks: [],
        }
        const nested = level < MAX_SUBTASK_DEPTH
        const below = subtasksOf(child.id, nested ? level + 1 : level, locked || childRecurrence !== null, seen)
        if (nested) planned.subtasks = below
        return nested ? [planned] : [planned, ...below]
      })

  // ── Abschnitte und Reihenfolge ──
  const sections: PlannedSection[] = []
  const sectionTarget = new Map<string, Ref | null>()
  const order = new Map<string, number>() // Todoist-ID (Abschnitt oder Aufgabe) → order
  for (const section of snapshot.sections) {
    const target = projectTarget.get(section.project_id)
    if (!target) { sectionTarget.set(section.id, null); continue } // Inbox oder gelöscht: Abschnitt entfällt
    const origin = todoist(section.id)
    const known = imported.sections.get(origin)
    if (known) {
      skipped++
      sectionTarget.set(section.id, known.deleted ? null : { id: known.id })
      continue
    }
    sectionTarget.set(section.id, { key: origin })
  }
  const byChildOrder = (a: TodoistItem, b: TodoistItem) => a.child_order - b.child_order
  /** Neue Wurzeln eines Blocks: offene nach `child_order`, dahinter die erledigten — wer eine wieder öffnet, findet sie in ihrem Abschnitt (ADR 0019). */
  const inBlock = (list: TodoistItem[]) => [...list.filter(r => !r.completed_at).sort(byChildOrder), ...list.filter(r => r.completed_at).sort(byChildOrder)]
  const freshRootsOf = (projectId: string) => roots.filter(r => r.project_id === projectId && !imported.tasks.has(todoist(r.id)))
  /** Ohne Abschnitt oder mit einem, der wegfällt (in HappyTask gelöscht): oben im Projekt. */
  const unsectioned = (list: TodoistItem[]) => list.filter(r => !r.section_id || !sectionTarget.get(r.section_id))
  const sectionsOf = (projectId: string) => snapshot.sections.filter(s => s.project_id === projectId).sort((a, b) => a.section_order - b.section_order)

  // Neues Projekt: durchgezählt — oben die Aufgaben ohne Abschnitt, dann je Abschnitt dessen Aufgaben.
  for (const projectId of newProjects) {
    let next = 0
    const own = freshRootsOf(projectId)
    for (const task of inBlock(unsectioned(own))) order.set(task.id, next++)
    for (const section of sectionsOf(projectId)) {
      order.set(section.id, next++)
      for (const task of inBlock(own.filter(r => r.section_id === section.id))) order.set(task.id, next++)
    }
  }
  /*
    Schon übernommenes Projekt (#317): Neues kommt in die Lücken dessen, was in HappyTask steht —
    nichts Vorhandenes wird umnummeriert. Eine Aufgabe ans Ende ihres Abschnitts, eine ohne Abschnitt
    ans Ende des Blocks oben, ein neuer Abschnitt samt Aufgaben hinter seinen Vorgänger in Todoist
    (den nächsten davor, den es in HappyTask noch gibt; sonst oben vor den ersten). Ohne Lage bleibt
    `order` leer, und die Datenbank hängt hinten an.
  */
  for (const [projectId, target] of projectTarget) {
    const layout = target && 'id' in target ? layouts?.get(target.id) : undefined
    if (!layout) continue
    const own = freshRootsOf(projectId)
    /** Anker (HappyTask-Abschnitt, `null` = oben) → Todoist-IDs, die an sein Ende kommen, in dieser Folge. */
    const queues = new Map<string | null, string[]>()
    const enqueue = (anchor: string | null, ids: string[]) => queues.set(anchor, [...(queues.get(anchor) ?? []), ...ids])
    enqueue(null, inBlock(unsectioned(own)).map(r => r.id))
    // Nur was in diesem Projekt steht, taugt als Anker: Ein in Todoist hierher verschobener Abschnitt liegt
    // in HappyTask noch im alten — seine neuen Aufgaben kommen dann ans Ende des Ankers davor.
    const here = new Set(layout.sections.map(s => s.id))
    let anchor: string | null = null
    for (const section of sectionsOf(projectId)) {
      const ref = sectionTarget.get(section.id)
      if (!ref) continue // in HappyTask gelöscht: seine Aufgaben stehen schon oben
      const tasks = inBlock(own.filter(r => r.section_id === section.id)).map(r => r.id)
      if ('id' in ref && here.has(ref.id)) anchor = ref.id
      enqueue(anchor, 'key' in ref ? [section.id, ...tasks] : tasks)
    }
    for (const [sectionId, ids] of queues) {
      const values = computeAppendOrders({ tasks: layout.tasks, sections: layout.sections, sectionId, count: ids.length })
      ids.forEach((id, i) => order.set(id, values[i]!))
    }
  }
  for (const section of snapshot.sections) {
    const ref = sectionTarget.get(section.id)
    if (!ref || !('key' in ref)) continue
    sections.push({
      importedFrom: ref.key,
      project: projectTarget.get(section.project_id)!,
      title: section.name,
      collapsed: section.is_collapsed,
      order: order.get(section.id) ?? null,
      description: section.description?.trim() ? section.description : null,
      archivedAt: section.is_archived ? archivedAtOf(section.archived_at) : null,
    })
  }

  // ── Aufgaben ──
  const tasks: PlannedTask[] = []
  for (const root of roots) {
    const origin = todoist(root.id)
    if (imported.tasks.has(origin)) { skipped++; continue }
    const inProject = projectsById.has(root.project_id)
    const project = inProject ? projectTarget.get(root.project_id) : null // unbekanntes Projekt: ohne Projekt
    if (project === undefined) { skipped++; continue } // Projekt in HappyTask gelöscht
    const completed = !!root.completed_at
    const recurrence = completed ? null : translateRecurrence(root.due)
    const start = root.due ? mapDate(root.due.date) : null
    const deadline = root.deadline ? mapDate(root.deadline.date) : null
    tasks.push({
      importedFrom: origin,
      project,
      section: project && root.section_id ? (sectionTarget.get(root.section_id) ?? null) : null,
      lifeSpace: project === null ? inboxLifeSpace : existingLifeSpace.get(root.project_id) ?? lifeSpaceFor(lifeSpaces, root.project_id),
      title: root.content,
      description: buildDescription(root, texts, recurrence !== null),
      comments: buildComments(notesByItem.get(root.id) ?? [], texts),
      reminders: remindersFor(root),
      attention: mapPriority(root.priority),
      startDate: start?.day ?? null,
      startTime: start?.time ?? null,
      dueDate: deadline?.day ?? null,
      ...mapDuration(root.duration),
      ...mapCounters(root),
      recurrence,
      completedAt: completed ? new Date(root.completed_at!) : null,
      order: order.get(root.id) ?? null,
      tags: tagRefs(root.labels),
      subtasks: subtasksOf(root.id, 1, recurrence !== null),
    })
  }
  // Unteraufgaben neuer Aufgaben unter schon übernommenen Aufgaben: bleiben weg (siehe Plan).
  for (const root of roots) {
    if (!imported.tasks.has(todoist(root.id))) continue
    skipped += childrenOf(root.id).length
  }

  const taskFills: TaskFill[] = []
  for (const item of allItems) {
    const row = imported.tasks.get(todoist(item.id)) ?? imported.subtasks.get(todoist(item.id))
    if (!row || row.deleted) continue
    // `undefined` = Zeile der alten Unteraufgaben-Tabelle: Sie hat diese Felder nicht.
    const changes: TaskFill['changes'] = {}
    if (item.duration?.unit === 'day' && row.estimatedDuration === null) {
      changes.estimatedDuration = item.duration.amount * MINUTES_PER_DAY
      changes.durationMode = 'days'
    }
    const counters = mapCounters(item)
    if (counters.completedCount !== null && row.completedCount === null) changes.completedCount = counters.completedCount
    if (counters.postponedCount !== null && row.postponedCount === null) changes.postponedCount = counters.postponedCount
    if (Object.keys(changes).length > 0) taskFills.push({ id: row.id, changes })
  }

  return { projects, sections, tags: plannedTags, tasks, taskFills, skipped }
}
